import { forwardRef, useState, type HTMLAttributes } from 'react';
import { cn } from '../lib/cn';
import { getInitials } from '../lib/hooks';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type AvatarTone = 'primary' | 'success' | 'warning' | 'error' | 'info' | 'neutral';

const SIZE: Record<AvatarSize, string> = {
  xs: 'size-5 text-[9px]',
  sm: 'size-[22px] text-10',
  md: 'size-7 text-11',
  lg: 'size-8 text-12',
  xl: 'size-10 text-14',
};

const TONE: Record<AvatarTone, string> = {
  primary: 'bg-primary-soft text-primary-text',
  success: 'bg-success-soft text-success-text',
  warning: 'bg-warning-soft text-warning-text',
  error: 'bg-error-soft text-error-text',
  info: 'bg-info-soft text-info-text',
  neutral: 'bg-surface-3 text-text-2',
};

export interface AvatarProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'color'> {
  /** Full name — used for the initials and the accessible name. */
  name: string;
  /** Explicit initials (default: first + last word initial). */
  initials?: string;
  /** Photo URL; falls back to initials if it fails to load. */
  src?: string;
  /** xs 20 · sm 22 (default, kanban) · md 28 · lg 32 · xl 40. */
  size?: AvatarSize;
  tone?: AvatarTone;
  /** Custom colours (CSS values), overriding `tone`. */
  color?: { bg: string; fg: string };
  /** Hide from assistive tech when the name is already shown next to it. */
  decorative?: boolean;
}

/** Circular initials avatar (600 weight). */
export const Avatar = forwardRef<HTMLSpanElement, AvatarProps>(function Avatar(
  {
    name,
    initials,
    src,
    size = 'sm',
    tone = 'primary',
    color,
    decorative = false,
    className,
    style,
    ...rest
  },
  ref,
) {
  const [failed, setFailed] = useState(false);
  const showImage = src && !failed;
  return (
    <span
      ref={ref}
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : name}
      aria-hidden={decorative || undefined}
      className={cn(
        'inline-grid shrink-0 select-none place-items-center overflow-hidden rounded-full font-semibold leading-none',
        SIZE[size],
        color ? undefined : TONE[tone],
        className,
      )}
      style={color ? { backgroundColor: color.bg, color: color.fg, ...style } : style}
      {...rest}
    >
      {showImage ? (
        <img src={src} alt="" className="size-full object-cover" onError={() => setFailed(true)} />
      ) : (
        (initials ?? getInitials(name))
      )}
    </span>
  );
});
